import { useState, useEffect, useCallback, useContext, useMemo } from 'react';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { GenerateReportButton } from '../components/GenerateReportButton';
import {
  FileText, Search, Library, Shield, Loader2, CheckCircle2, XCircle, ChevronRight,
  Paperclip, AlertTriangle, Users, Settings2, Download, Zap, Pencil,
} from 'lucide-react';

const IMPL_COLORS = {
  'Compliant':             'bg-emerald-100 text-emerald-800 border-emerald-200',
  'Partially Compliant':   'bg-blue-100 text-blue-800 border-blue-200',
  'Non Compliant':         'bg-red-100 text-red-800 border-red-200',
  'Not Assessed':          'bg-slate-100 text-slate-700 border-slate-200',
  'Not Applicable':        'bg-slate-50 text-slate-500 border-slate-200',
};
const PRIORITY_COLORS = {
  Critical: 'bg-red-100 text-red-800 border-red-200',
  High: 'bg-orange-100 text-orange-800 border-orange-200',
  Medium: 'bg-amber-100 text-amber-800 border-amber-200',
  Low: 'bg-slate-100 text-slate-700 border-slate-200',
};

function StatCard({ label, value, tone = 'slate', testid }) {
  const toneMap = {
    slate: 'text-slate-900', emerald: 'text-emerald-600',
    blue: 'text-blue-600', amber: 'text-amber-600', red: 'text-red-600',
  };
  return (
    <Card className="border-slate-200">
      <CardContent className="py-4 px-5">
        <div className={`text-2xl font-bold ${toneMap[tone]}`} data-testid={testid}>{value}</div>
        <div className="text-xs text-slate-500 mt-0.5">{label}</div>
      </CardContent>
    </Card>
  );
}

function SoARowDetail({ open, onOpenChange, row, framework, canWrite, onNotesSaved }) {
  const [notes, setNotes] = useState(row?.notes || '');
  const [saving, setSaving] = useState(false);
  useEffect(() => { setNotes(row?.notes || ''); }, [row]);
  if (!row) return null;
  const saveNotes = async () => {
    setSaving(true);
    try {
      const res = await axios.patch(
        `${API}/soa/${encodeURIComponent(framework.id)}/requirements/${row.requirement_id}/notes`,
        { notes },
      );
      toast.success('Comment saved');
      onNotesSaved?.(row.requirement_id, res.data?.notes ?? notes);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save comment');
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="soa-row-detail-dialog" className="max-w-4xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <span className="font-mono text-slate-500 text-base">{row.external_code}</span>
            <span>{row.title || <em className="text-slate-400 font-normal">Official title not set</em>}</span>
          </DialogTitle>
          <DialogDescription>
            {framework?.short_name} · {row.category || 'Uncategorized'}
          </DialogDescription>
        </DialogHeader>

        <div className="mt-3 flex flex-wrap gap-2">
          <Badge className={`${row.applicable ? 'bg-blue-100 text-blue-800 border-blue-200' : 'bg-slate-100 text-slate-600 border-slate-200'} border`}>
            {row.applicability_status}
          </Badge>
          <Badge className={`${IMPL_COLORS[row.implementation_status] || ''} border`}>
            {row.implementation_status}
          </Badge>
          {row.evidence_count > 0 && (
            <Badge className="bg-blue-50 text-blue-700 border-blue-200 border">
              <Paperclip className="w-3 h-3 mr-1" /> {row.evidence_count} evidence
            </Badge>
          )}
          {row.gap_count > 0 && (
            <Badge className="bg-red-50 text-red-700 border-red-200 border">
              <AlertTriangle className="w-3 h-3 mr-1" /> {row.gap_count} open gap{row.gap_count > 1 ? 's' : ''}
            </Badge>
          )}
        </div>

        {row.applicability_justification && (
          <section className="mt-4 rounded-lg border border-slate-200 bg-slate-50 p-3">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-600 mb-1">
              Applicability justification
            </h3>
            <p className="text-sm text-slate-800 whitespace-pre-line">{row.applicability_justification}</p>
          </section>
        )}

        <section className="mt-4 rounded-lg border border-slate-200 bg-white p-3" data-testid="soa-comments-section">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-600 mb-2 flex items-center gap-1">
            <Pencil className="w-3 h-3" /> Reference
          </h3>
          <textarea
            className="w-full h-24 text-sm rounded border border-slate-300 p-2 focus:outline-none focus:ring-1 focus:ring-slate-400 disabled:bg-slate-50"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            disabled={!canWrite || saving}
            data-testid={`soa-comment-input-${row.external_code}`}
          />
          <div className="flex justify-end mt-2">
            <Button
              size="sm"
              onClick={saveNotes}
              disabled={!canWrite || saving || (notes || '') === (row.notes || '')}
              data-testid={`soa-comment-save-${row.external_code}`}
            >
              {saving ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : null} Save
            </Button>
          </div>
        </section>

        {row.implementation_description && (
          <section className="mt-3 rounded-lg border border-slate-200 bg-white p-3">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-600 mb-1">
              Implementation description
            </h3>
            <p className="text-sm text-slate-800 whitespace-pre-line">{row.implementation_description}</p>
          </section>
        )}

        {row.owners?.length > 0 && (
          <section className="mt-3 rounded-lg border border-slate-200 bg-white p-3">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-600 mb-1 flex items-center gap-1">
              <Users className="w-3 h-3" /> Owners
            </h3>
            <p className="text-sm text-slate-800">{row.owners.join(', ')}</p>
          </section>
        )}

        <section className="mt-3 rounded-lg border border-slate-200 bg-white p-3">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-600 mb-2">
            Related Master Controls ({row.related_master_controls?.length || 0})
          </h3>
          {(!row.related_master_controls || row.related_master_controls.length === 0) ? (
            <p className="text-sm text-slate-500 italic">No master controls mapped.</p>
          ) : (
            <div className="border border-slate-200 rounded overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[90px]">ID</TableHead>
                    <TableHead>Master control</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-center w-[80px]">Evidence</TableHead>
                    <TableHead>Owner</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {row.related_master_controls.map((c) => (
                    <TableRow key={c.control_id} data-testid={`soa-related-mc-${c.control_id}`}>
                      <TableCell className="font-mono text-slate-600">{c.control_id}</TableCell>
                      <TableCell>
                        <div className="font-medium text-slate-900">{c.name}</div>
                        <div className="text-xs text-slate-500 line-clamp-1">{c.description}</div>
                      </TableCell>
                      <TableCell>
                        <Badge className={`${IMPL_COLORS[c.assessment_status] || ''} border text-xs`}>
                          {c.assessment_status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        {c.evidence_count > 0 ? (
                          <Badge className="bg-blue-50 text-blue-700 border-blue-200 border text-xs">
                            {c.evidence_count}
                          </Badge>
                        ) : (
                          <span className="text-slate-300">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-xs text-slate-700">
                        {c.owner || <span className="text-slate-400 italic">—</span>}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </section>

        {row.related_gaps?.length > 0 && (
          <section className="mt-3 rounded-lg border border-red-200 bg-red-50 p-3">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-red-700 mb-2 flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" /> Related open gaps ({row.related_gaps.length})
            </h3>
            <ul className="space-y-2">
              {row.related_gaps.map((g) => (
                <li
                  key={g.control_id}
                  className="text-sm bg-white border border-red-100 rounded p-2"
                  data-testid={`soa-related-gap-${g.control_id}`}
                >
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-slate-700">{g.control_id}</span>
                    <Badge className={`${PRIORITY_COLORS[g.priority] || ''} border text-xs`}>{g.priority}</Badge>
                    <Badge variant="outline" className="text-xs">{g.gap_reason}</Badge>
                    {g.quick_win && (
                      <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 border text-xs">
                        <Zap className="w-3 h-3 mr-1" /> Quick win
                      </Badge>
                    )}
                  </div>
                  {g.corrective_action && (
                    <div className="mt-1 text-xs text-slate-700"><strong>Action:</strong> {g.corrective_action}</div>
                  )}
                  {g.target_close_date && (
                    <div className="text-xs text-slate-600">Target close: {g.target_close_date}</div>
                  )}
                  {g.gap_owner && <div className="text-xs text-slate-600">Owner: {g.gap_owner}</div>}
                </li>
              ))}
            </ul>
          </section>
        )}

        {row.evidence?.length > 0 && (
          <section className="mt-3 rounded-lg border border-slate-200 bg-white p-3">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-600 mb-2 flex items-center gap-1">
              <Paperclip className="w-3 h-3" /> Evidence ({row.evidence.length})
            </h3>
            <ul className="text-sm space-y-1">
              {row.evidence.map((f) => (
                <li key={`${f.master_control_id}-${f.document_id}`} className="text-xs" data-testid={`soa-evidence-${f.document_id}`}>
                  <span className="font-mono text-slate-500">{f.master_control_id}</span> ·
                  <span className="ml-1 text-slate-800">{f.file_name}</span>
                  {f.uploaded_at && <span className="text-slate-500 ml-1">({f.uploaded_at.slice(0, 10)})</span>}
                </li>
              ))}
            </ul>
          </section>
        )}
      </DialogContent>
    </Dialog>
  );
}

function SettingsDialog({ open, onOpenChange, enabled, onSaved }) {
  const [checked, setChecked] = useState(enabled);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setChecked(enabled); }, [enabled]);
  const save = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API}/soa/settings`, { use_new_soa_generator: checked });
      toast.success(`New SoA Generator turned ${checked ? 'ON' : 'OFF'}`);
      onSaved?.(checked);
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="soa-settings-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle>SoA Generator settings</DialogTitle>
          <DialogDescription>
            Toggle the new derived Statement of Applicability generator on or off for this tenant.
          </DialogDescription>
        </DialogHeader>
        <div className="mt-4 flex items-center justify-between rounded-lg border border-slate-200 p-3">
          <div className="pr-4">
            <div className="text-sm font-medium text-slate-900">Use New SoA Generator</div>
            <div className="text-xs text-slate-500 mt-1">
              When ON, the SoA is generated live from Master Control assessments, mappings, evidence &amp; gaps.
              No duplicate data is stored.
            </div>
          </div>
          <Switch
            checked={checked}
            onCheckedChange={setChecked}
            data-testid="soa-feature-toggle"
          />
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="soa-settings-save-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null} Save
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function SoAPage() {
  const { user, viewingOrg } = useContext(AuthContext);
  const canWrite = ['admin', 'super_admin'].includes(user?.role);
  const canManage = user?.role === 'admin' || user?.role === 'super_admin';
  const [frameworks, setFrameworks] = useState([]);
  const [frameworkId, setFrameworkId] = useState('ISO27001:2022');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingCode, setSavingCode] = useState(null);

  const updateSoaStatus = async (controlIds, newStatus, externalCode) => {
    setSavingCode(externalCode);
    try {
      // Update every mapped master control's assessment status
      await Promise.all(
        controlIds.map((cid) =>
          axios.put(`${API}/master-controls/${cid}/assessment`, { status: newStatus })
        )
      );
      toast.success(`${externalCode} → ${newStatus}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update status');
    } finally {
      setSavingCode(null);
    }
  };
  const [featureEnabled, setFeatureEnabled] = useState(true);
  const [soaVersion, setSoaVersion] = useState('1.0');
  const [soaOwner, setSoaOwner] = useState('');
  const [soaDate, setSoaDate] = useState('');
  const [editingMeta, setEditingMeta] = useState(false);
  const [metaDraft, setMetaDraft] = useState({ version: '', owner: '', date: '' });
  const [savingMeta, setSavingMeta] = useState(false);
  const [search, setSearch] = useState('');
  const [applicability, setApplicability] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [rowDetail, setRowDetail] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);

  // Load frameworks + settings on mount
  useEffect(() => {
    (async () => {
      try {
        const [fRes, sRes] = await Promise.all([
          axios.get(`${API}/soa/frameworks`),
          axios.get(`${API}/soa/settings`),
        ]);
        setFrameworks(fRes.data || []);
        setFeatureEnabled(sRes.data?.use_new_soa_generator ?? true);
        setSoaVersion(sRes.data?.soa_version || '1.0');
        setSoaOwner(sRes.data?.soa_owner || '');
        setSoaDate(sRes.data?.soa_date || '');
      } catch {
        toast.error('Failed to load SoA settings');
      }
    })();
  }, [viewingOrg]);

  const startEditMeta = () => {
    setMetaDraft({ version: soaVersion, owner: soaOwner, date: soaDate });
    setEditingMeta(true);
  };

  const cancelEditMeta = () => {
    setEditingMeta(false);
    setMetaDraft({ version: '', owner: '', date: '' });
  };

  const saveMeta = async () => {
    setSavingMeta(true);
    try {
      const res = await axios.patch(`${API}/soa/settings`, {
        soa_version: metaDraft.version.trim(),
        soa_owner: metaDraft.owner.trim(),
        soa_date: metaDraft.date,
      });
      setSoaVersion(res.data?.soa_version || '1.0');
      setSoaOwner(res.data?.soa_owner || '');
      setSoaDate(res.data?.soa_date || '');
      setEditingMeta(false);
      toast.success('SoA details saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally {
      setSavingMeta(false);
    }
  };

  const load = useCallback(async () => {
    if (!frameworkId) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search.trim()) params.set('search', search.trim());
      if (applicability !== 'all') params.set('applicability', applicability);
      if (statusFilter !== 'all') params.set('status', statusFilter);
      const res = await axios.get(
        `${API}/soa/${encodeURIComponent(frameworkId)}?${params.toString()}`,
      );
      setData(res.data);
      setFeatureEnabled(res.data?.feature_enabled ?? true);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to generate SoA');
    } finally {
      setLoading(false);
    }
  }, [frameworkId, search, applicability, statusFilter, viewingOrg]);

  useEffect(() => { load(); }, [load]);

  const exportCsv = () => {
    if (!data?.rows?.length) return;
    const header = [
      'External Code', 'Title', 'Category', 'Applicable', 'Applicability Justification',
      'Implementation Status', 'Implementation Description', 'Owners',
      'Related Master Controls', 'Evidence Count', 'Open Gaps',
    ];
    const escape = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const rows = data.rows.map((r) => [
      r.external_code, r.title || '', r.category || '',
      r.applicability_status, r.applicability_justification || '',
      r.implementation_status, r.implementation_description || '',
      (r.owners || []).join('; '),
      (r.related_master_controls || []).map((c) => c.control_id).join('; '),
      r.evidence_count, r.gap_count,
    ].map(escape).join(','));
    const csv = [header.map(escape).join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `SoA_${data.framework.id}_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success('SoA exported');
  };

  const s = data?.summary || {};

  const activeFilterCount = useMemo(() => [
    search.trim(), applicability !== 'all', statusFilter !== 'all',
  ].filter(Boolean).length, [search, applicability, statusFilter]);

  return (
    <div className="space-y-6" data-testid="soa-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <Library className="w-4 h-4" />
            <span>Compliance Engine v2</span>
            <span className="text-slate-300">/</span>
            <span>Statement of Applicability</span>
          </div>
          <h1 className="text-3xl font-bold text-slate-900 mt-1 flex items-center gap-2">
            <FileText className="w-7 h-7 text-slate-700" />
            Statement of Applicability
          </h1>
          <p className="text-sm text-slate-600 mt-1 max-w-2xl">
            Auto-generated from your Master Control assessments, mappings, evidence and gaps.
            One place, no duplicate data.
          </p>
          {/* SoA Version + Owner — editable inline by admins */}
          <div
            className="mt-3 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm"
            data-testid="soa-meta-strip"
          >
            {editingMeta ? (
              <>
                <div className="flex items-center gap-2">
                  <span className="text-xs uppercase font-semibold text-slate-500 tracking-wide">Version</span>
                  <Input
                    value={metaDraft.version}
                    onChange={(e) => setMetaDraft({ ...metaDraft, version: e.target.value })}
                    className="h-8 w-28"
                    placeholder="e.g., 1.2"
                    data-testid="soa-version-input"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs uppercase font-semibold text-slate-500 tracking-wide">Owner</span>
                  <Input
                    value={metaDraft.owner}
                    onChange={(e) => setMetaDraft({ ...metaDraft, owner: e.target.value })}
                    className="h-8 w-64"
                    placeholder="e.g., Jane Doe (CISO)"
                    data-testid="soa-owner-input"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs uppercase font-semibold text-slate-500 tracking-wide">SOA Date</span>
                  <Input
                    type="date"
                    value={metaDraft.date}
                    onChange={(e) => setMetaDraft({ ...metaDraft, date: e.target.value })}
                    className="h-8 w-44"
                    data-testid="soa-date-input"
                  />
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    size="sm"
                    onClick={saveMeta}
                    disabled={savingMeta}
                    className="h-8 bg-slate-900 hover:bg-slate-800 text-white"
                    data-testid="soa-meta-save-btn"
                  >
                    {savingMeta ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : null} Save
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={cancelEditMeta}
                    className="h-8"
                    data-testid="soa-meta-cancel-btn"
                  >
                    Cancel
                  </Button>
                </div>
              </>
            ) : (
              <>
                <div className="flex items-center gap-2" data-testid="soa-version-display">
                  <span className="text-xs uppercase font-semibold text-slate-500 tracking-wide">Version</span>
                  <span className="text-sm font-medium text-slate-900">{soaVersion || '—'}</span>
                </div>
                <div className="flex items-center gap-2" data-testid="soa-owner-display">
                  <span className="text-xs uppercase font-semibold text-slate-500 tracking-wide">Owner</span>
                  <span className="text-sm font-medium text-slate-900">{soaOwner || '—'}</span>
                </div>
                <div className="flex items-center gap-2" data-testid="soa-date-display">
                  <span className="text-xs uppercase font-semibold text-slate-500 tracking-wide">SOA Date:</span>
                  <span className="text-sm font-medium text-slate-900">{soaDate ? soaDate.split('-').reverse().join('/') : '—'}</span>
                </div>
                {canManage && (
                  <button
                    type="button"
                    onClick={startEditMeta}
                    className="inline-flex items-center gap-1 text-sm text-teal-700 hover:text-teal-800 hover:underline"
                    data-testid="soa-meta-edit-btn"
                  >
                    <Pencil className="w-3.5 h-3.5" />
                    Edit
                  </button>
                )}
              </>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Select value={frameworkId} onValueChange={setFrameworkId}>
            <SelectTrigger data-testid="soa-framework-select" className="w-[220px]">
              <SelectValue placeholder="Framework" />
            </SelectTrigger>
            <SelectContent>
              {frameworks.map((f) => (
                <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={exportCsv} disabled={!data?.rows?.length} data-testid="soa-export-csv-btn">
            <Download className="w-4 h-4 mr-1" /> Export CSV
          </Button>
          {frameworkId && (
            <GenerateReportButton
              options={[{ key: 'soa', label: 'SoA Report (PDF)', report_type: 'soa', framework_id: frameworkId }]}
              variant="default"
              className="bg-orange-600 hover:bg-orange-700 text-white"
              testid="soa-generate-report-btn"
            />
          )}
          {canManage && (
            <Button variant="outline" onClick={() => setSettingsOpen(true)} data-testid="soa-settings-btn">
              <Settings2 className="w-4 h-4 mr-1" /> Settings
            </Button>
          )}
        </div>
      </div>

      {!featureEnabled && (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="p-4 text-sm text-amber-900 flex items-center gap-3">
            <XCircle className="w-5 h-5 text-amber-700 shrink-0" />
            <div>
              <strong>New SoA Generator is OFF.</strong>{' '}
              {canManage
                ? 'Click Settings above to enable it.'
                : 'Ask an admin to enable it in SoA Settings.'}
            </div>
          </CardContent>
        </Card>
      )}

      {featureEnabled && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
            <StatCard label="Total Requirements" value={data?.total_requirements ?? 0} testid="soa-total" />
            <StatCard label="Applicable" value={s.applicable ?? 0} tone="blue" testid="soa-applicable" />
            <StatCard label="Not Applicable" value={s.not_applicable ?? 0} tone="slate" testid="soa-not-applicable" />
            <StatCard label="Compliant" value={s.implemented ?? 0} tone="emerald" testid="soa-implemented" />
            <StatCard label="Partially Compliant" value={s.partially_implemented ?? 0} tone="blue" testid="soa-partial" />
          </div>

          <Card className="border-slate-200">
            <CardContent className="p-4 flex flex-col md:flex-row gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  data-testid="soa-search-input"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by code, title, master control, description…"
                  className="pl-9"
                />
              </div>
              <Select value={applicability} onValueChange={setApplicability}>
                <SelectTrigger data-testid="soa-filter-applicability" className="w-[180px]">
                  <SelectValue placeholder="Applicability" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="Applicable">Applicable</SelectItem>
                  <SelectItem value="Partially applicable">Partially applicable</SelectItem>
                  <SelectItem value="Not Applicable">Not Applicable</SelectItem>
                </SelectContent>
              </Select>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger data-testid="soa-filter-status" className="w-[200px]">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  {['Compliant', 'Partially Compliant', 'Non Compliant', 'Not Assessed', 'Not Applicable'].map((v) => (
                    <SelectItem key={v} value={v}>{v}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </CardContent>
          </Card>

          <div className="flex items-center justify-between text-xs text-slate-500">
            <span>
              Showing <span className="font-semibold text-slate-800" data-testid="soa-matching-count">{data?.matching_requirements ?? 0}</span> of {data?.total_requirements ?? 0} requirements
              {activeFilterCount > 0 && ` · ${activeFilterCount} filter${activeFilterCount > 1 ? 's' : ''} active`}
            </span>
            {data?.generated_at && (
              <span>Generated: {new Date(data.generated_at).toLocaleString()}</span>
            )}
          </div>

          <Card className="border-slate-200">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[110px]">Code</TableHead>
                      <TableHead className="min-w-[220px]">Requirement</TableHead>
                      <TableHead>Applicability</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-center">Evidence</TableHead>
                      <TableHead className="text-center">Gaps</TableHead>
                      <TableHead>Owner(s)</TableHead>
                      <TableHead className="w-[40px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center py-12 text-slate-500">
                          <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Generating SoA…
                        </TableCell>
                      </TableRow>
                    ) : (data?.rows || []).length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center py-16 text-slate-500">
                          <CheckCircle2 className="w-8 h-8 text-emerald-500 inline mb-2" />
                          <div>No SoA rows match the current filters.</div>
                        </TableCell>
                      </TableRow>
                    ) : (
                      data.rows.map((r) => (
                        <TableRow
                          key={r.requirement_id}
                          className="cursor-pointer hover:bg-slate-50"
                          onClick={() => { setRowDetail(r); setDetailOpen(true); }}
                          data-testid={`soa-row-${r.external_code}`}
                        >
                          <TableCell className="font-mono text-slate-700">{r.external_code}</TableCell>
                          <TableCell>
                            {r.title ? (
                              <div className="font-medium text-slate-900">{r.title}</div>
                            ) : (
                              <em className="text-slate-400">Official title not set</em>
                            )}
                            {r.category && <div className="text-xs text-slate-500">{r.category}</div>}
                          </TableCell>
                          <TableCell>
                            <Badge className={`${r.applicable ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-slate-100 text-slate-600 border-slate-200'} border text-xs`}>
                              {r.applicability_status}
                            </Badge>
                          </TableCell>
                          <TableCell onClick={(e) => e.stopPropagation()}>
                            {(() => {
                              // Extract base status (strip "(Recommended)" suffix if present)
                              const isRecOnly = r.implementation_status?.endsWith('(Recommended)');
                              const baseStatus = isRecOnly
                                ? r.implementation_status.replace(' (Recommended)', '').trim()
                                : r.implementation_status;
                              const options = ['Compliant', 'Partially Compliant', 'Non Compliant', 'Not Assessed', 'Not Applicable'];
                              const trigCls = IMPL_COLORS[baseStatus] || IMPL_COLORS['Not Assessed'];
                              const cids = (r.related_master_controls || []).map((c) => c.control_id);
                              return (
                                <div className="flex flex-col items-start gap-0.5">
                                  <Select
                                    value={options.includes(baseStatus) ? baseStatus : 'Not Assessed'}
                                    onValueChange={(v) => updateSoaStatus(cids, v, r.external_code)}
                                    disabled={!canWrite || savingCode === r.external_code || cids.length === 0}
                                  >
                                    <SelectTrigger
                                      className={`h-7 text-xs font-medium border ${trigCls} px-2 min-w-[160px]`}
                                      data-testid={`soa-status-select-${r.external_code}`}
                                    >
                                      {savingCode === r.external_code ? (
                                        <span className="inline-flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Saving…</span>
                                      ) : (
                                        <SelectValue />
                                      )}
                                    </SelectTrigger>
                                    <SelectContent>
                                      {options.map((s) => (
                                        <SelectItem key={s} value={s} data-testid={`soa-status-option-${r.external_code}-${s.replace(/\s+/g, '-')}`}>{s}</SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                  {isRecOnly && (
                                    <span className="text-[10px] text-indigo-600 font-medium">(Recommended)</span>
                                  )}
                                </div>
                              );
                            })()}
                          </TableCell>
                          <TableCell className="text-center">
                            {r.evidence_count > 0 ? (
                              <Badge className="bg-blue-50 text-blue-700 border-blue-200 border text-xs">
                                <Paperclip className="w-3 h-3 mr-1" />{r.evidence_count}
                              </Badge>
                            ) : (
                              <span className="text-slate-300 text-xs">—</span>
                            )}
                          </TableCell>
                          <TableCell className="text-center">
                            {r.gap_count > 0 ? (
                              <Badge className="bg-red-50 text-red-700 border-red-200 border text-xs">
                                <AlertTriangle className="w-3 h-3 mr-1" />{r.gap_count}
                              </Badge>
                            ) : (
                              <span className="text-slate-300 text-xs">—</span>
                            )}
                          </TableCell>
                          <TableCell className="text-xs text-slate-700">
                            {(r.owners || []).length > 0 ? r.owners.join(', ') : <span className="text-slate-400 italic">—</span>}
                          </TableCell>
                          <TableCell>
                            <ChevronRight className="w-4 h-4 text-slate-400" />
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </>
      )}

      <SoARowDetail
        open={detailOpen}
        onOpenChange={setDetailOpen}
        row={rowDetail}
        framework={data?.framework}
        canWrite={canWrite}
        onNotesSaved={(reqId, newNotes) => {
          setData((d) => d ? ({
            ...d,
            rows: d.rows.map((r) => r.requirement_id === reqId ? { ...r, notes: newNotes } : r),
          }) : d);
          setRowDetail((r) => r && r.requirement_id === reqId ? { ...r, notes: newNotes } : r);
        }}
      />
      <SettingsDialog
        open={settingsOpen}
        onOpenChange={setSettingsOpen}
        enabled={featureEnabled}
        onSaved={(v) => { setFeatureEnabled(v); load(); }}
      />
    </div>
  );
}
